import { authFetch } from '../api/csrf';
import { useQuery, useMutation, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import leadService from '../api/crm/leads/leadService';
import leadActivityService from '../api/crm/leads/leadActivityService';
import emailLogService from '../api/crm/leads/emailLogService';

/**
 * Hook for fetching a single lead by ID
 */
export const useLead = (leadId) => {
    return useQuery({
        queryKey: ['lead', leadId],
        queryFn: async () => {
            const result = await leadService.getLeadById(leadId);
            if (!result.success) throw new Error(result.error);
            return result.lead;
        },
        enabled: !!leadId,
        staleTime: 0,
    });
};

/**
 * Hook for fetching lead activities with infinite scroll support
 */
export const useLeadActivities = (leadId, filters = {}) => {
    return useInfiniteQuery({
        queryKey: ['lead-activities', leadId, filters],
        queryFn: async ({ pageParam = 1 }) => {
            const result = await leadActivityService.getLeadActivities(leadId, {
                ...filters,
                page: pageParam,
                limit: filters.limit || 10,
                sortBy: filters.sortBy || 'performedAt',
                sortOrder: filters.sortOrder || 'desc',
            });
            if (!result.success) throw new Error(result.error);
            return result;
        },
        getNextPageParam: (lastPage) => {
            const { pagination } = lastPage;
            if (pagination && pagination.currentPage < pagination.totalPages) {
                return pagination.currentPage + 1;
            }
            return undefined;
        },
        initialPageParam: 1,
        enabled: !!leadId,
    });
};

/**
 * Hook for fetching master sources from config
 */
export const useMasterSources = () => {
    return useQuery({
        queryKey: ['master-sources'],
        queryFn: async () => {
            const response = await authFetch(`${process.env.REACT_APP_API_BASE_URL}/config/sources`, {
        credentials: 'include',
                headers: {
                    }
            });
            const result = await response.json();
            if (!result.success) throw new Error(result.error || 'Failed to fetch sources');
            return result.data || [];
        },
        staleTime: 0,
    });
};

/**
 * Hook for fetching lead remarks with infinite scroll/pagination support
 */
export const useLeadRemarks = (leadId, limit = 10, enabled = true) => {
    return useInfiniteQuery({
        queryKey: ['lead-remarks', leadId],
        queryFn: async ({ pageParam = 1 }) => {
            const result = await leadService.getLeadRemarks(leadId, pageParam, limit);
            if (!result.success) throw new Error(result.error);
            return result;
        },
        getNextPageParam: (lastPage) => {
            const { pagination } = lastPage;
            if (pagination && pagination.currentPage < pagination.totalPages) {
                return pagination.currentPage + 1;
            }
            return undefined;
        },
        initialPageParam: 1,
        enabled: !!leadId && enabled,
    });
};

/**
 * Hook for lead mutations (initiate call, send email, etc.)
 */
export const useLeadMutations = () => {
    const queryClient = useQueryClient();

    // Mutation for initiating a call
    const initiateCallMutation = useMutation({
        mutationFn: (leadId) => leadService.initiateCall(leadId),
        onSuccess: (_, leadId) => {
            queryClient.invalidateQueries({ queryKey: ['lead-activities', leadId] });
        },
    });

    // Mutation for sending an email
    const sendEmailMutation = useMutation({
        mutationFn: ({ leadId, emailData }) => emailLogService.sendEmail(leadId, emailData),
        onSuccess: (_, { leadId }) => {
            queryClient.invalidateQueries({ queryKey: ['lead-activities', leadId] });
        },
    });

    // Mutation for sending WhatsApp
    const sendWhatsAppMutation = useMutation({
        mutationFn: ({ leadId, activityData }) => leadActivityService.createActivity(leadId, activityData),
        onSuccess: (_, { leadId }) => {
            queryClient.invalidateQueries({ queryKey: ['lead-activities', leadId] });
        },
    });

    // Mutation for scheduling a meeting
    const scheduleMeetingMutation = useMutation({
        mutationFn: ({ leadId, activityData }) => leadActivityService.createActivity(leadId, activityData),
        onSuccess: (_, { leadId }) => {
            queryClient.invalidateQueries({ queryKey: ['lead-activities', leadId] });
        },
    });

    // Mutation for adding a remark
    const addRemarkMutation = useMutation({
        mutationFn: ({ leadId, remark, file }) => leadService.addRemark(leadId, remark, file),
        onSuccess: (_, { leadId }) => {
            queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
            queryClient.invalidateQueries({ queryKey: ['lead-remarks', leadId] });
        },
    });

    return {
        initiateCall: initiateCallMutation,
        sendEmail: sendEmailMutation,
        sendWhatsApp: sendWhatsAppMutation,
        scheduleMeeting: scheduleMeetingMutation,
        addRemark: addRemarkMutation
    };
};
